import type { components } from "@/api/types";

export type ShowcaseBlock = components["schemas"]["ShowcaseBlock"];

/**
 * Блоки экрана робота, которые оператор может выключить (ТЗ §26).
 *
 * Порядок — как на экране пользователя, чтобы список отметок читался тем же
 * взглядом, что и результат.
 */
export const SHOWCASE_BLOCKS: readonly ShowcaseBlock[] = [
  "win_rate",
  "aum",
  "now",
  "trades",
  "top_users",
  "distribution",
];

type Stored = Record<string, boolean> | undefined;

/**
 * Хранимая запись → отметки формы.
 *
 * Отсутствующий ключ означает «показывать» — это свойство контракта, а не
 * умолчание формы: так же его читают и бэкенд, и мини-апп.
 */
export function blocksToForm(stored: Stored): ShowcaseBlock[] {
  return SHOWCASE_BLOCKS.filter((code) => stored?.[code] !== false);
}

/**
 * Отметки формы → тело запроса.
 *
 * В тело уезжают ТОЛЬКО выключенные блоки: явный `true` означает ровно то же,
 * что отсутствие ключа, и писать его значит засорять журнал before/after
 * строками, за которыми ничего не стоит. Отсюда же главное свойство: робот, у
 * которого оператор не трогал отметки, уходит обратно с той же записью, с
 * какой пришёл, — сохранение формы не может случайно включить скрытый блок.
 *
 * Коды, которых админка ещё не знает (бэкенд ушёл вперёд), переносятся как
 * есть — по той же причине.
 */
export function blocksToBody(
  visible: ShowcaseBlock[] | undefined,
  stored: Stored,
): Record<string, boolean> {
  const shown = new Set(visible ?? SHOWCASE_BLOCKS);
  const hidden = Object.fromEntries(
    SHOWCASE_BLOCKS.filter((code) => !shown.has(code)).map((code) => [code, false]),
  );
  const unknown = Object.fromEntries(
    Object.entries(stored ?? {}).filter(
      ([code]) => !SHOWCASE_BLOCKS.includes(code as ShowcaseBlock),
    ),
  );
  return { ...unknown, ...hidden };
}
